import { useCallback, useMemo } from 'react';
import { capitalize } from 'lodash-es';
import {
  AnalyticsCommonTypes,
  PracticeAnalyticsAggregations,
  PracticeAnalyticsApi,
  PracticeAnalyticsTypes,
} from '@frontend/api-analytics';
import {
  useAnalyticsOrgLocations,
  ColumnHeaderInfoTip,
  InfoTipPopover,
  Status,
  TabbedTable,
  UserCard,
  LocationChip,
} from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { TableColumnConfig, TableLoadingSkeleton, emptyStateGraphics } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../hooks/use-show-bulk-message-button';
import { trackingIds } from '../../tracking-ids';
import { drillDownTableSize } from '../../views/common-styles';
import { TableActions } from '../table-actions';
import { BulkMessageButton } from './bulk-message-button';
import { getLast24WeeksDates } from './helpers/get-last-24-weeks-dates';
import { collectPatientIds, deriveExportValue } from './helpers/patients';
import { usePatientsLastContacted } from './hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { SmallModePatientsList } from './small-mode-patients-list';

type Tab = 'all' | 'unscheduled';

type Props = {
  data?: ReturnType<typeof PracticeAnalyticsAggregations.activePatientsDetails>;
  isExportEnabled?: boolean;
  isLoading?: boolean;
};

interface LargeTableProps extends PracticeAnalyticsTypes.LargeModeTableProps<Tab> {
  patients: {
    all: PracticeAnalyticsTypes.PatientInfo[];
    insurances?: Record<string, string>;
    unscheduled: PracticeAnalyticsTypes.PatientInfo[];
  };
}

export const ActivePatientsTable = ({ data, isExportEnabled, isLoading }: Props) => {
  const { t } = useTranslation('analytics');
  const { filters, isDemoAccount } = usePracticeAnalyticsShallowStore('filters', 'isDemoAccount');
  const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });

  const { isSmallMode } = useIsSmallMode();
  const isBulkMessagingEnabled = useShowBulkMessageButton();

  // Collect all patients from all buckets and separate them into all and unscheduled categories
  const patients = useMemo(() => {
    const unscheduled: PracticeAnalyticsTypes.PatientInfo[] = [];

    const allPatients = data?.aggregatedData.patientDemographics.buckets
      .map(({ subbuckets }) => {
        return subbuckets
          .map(({ persons }) => {
            persons?.forEach((person) => {
              if (!person.nextVisit) {
                unscheduled.push(person);
              }
            });
            return persons;
          })
          .flat();
      })
      .flat() as PracticeAnalyticsTypes.PatientInfo[];

    const insurances = data?.aggregatedData.patientDemographics.details.insuranceProviders.reduce(
      (acc, { label, persons }) => {
        persons?.forEach(({ id }) => {
          acc[id] = capitalize(label);
        });
        return acc;
      },
      {} as Record<string, string>
    );

    return {
      all: allPatients,
      insurances,
      unscheduled,
    };
  }, [data?.aggregatedData]);

  const lastContactedParams = useMemo(() => {
    const { startDate, endDate } = getLast24WeeksDates();

    return {
      endTime: endDate,
      patientIds: collectPatientIds(patients.all),
      startTime: startDate,
    };
  }, [patients.all]);

  const { isLoading: isLoadingLastContacted, lastContactedDates } = usePatientsLastContacted(lastContactedParams);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'Gender',
          'Age',
          'Status',
          'Last Contacted',
          'Insurance',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            insuranceNames: patients.insurances,
            lastContactedDates,
            locationNames,
          }),
      }),
    [filters.locations, lastContactedDates, locationNames, patients.insurances]
  );

  const tabs = useMemo(
    () => ({
      all: {
        data: patients?.all || [],
        label: t('All'),
        trackingId: trackingIds.practiceAnalytics.allActivePatientsTab,
      },
      unscheduled: {
        data: patients?.unscheduled || [],
        label: t('Unscheduled'),
        trackingId: trackingIds.practiceAnalytics.unscheduledActivePatientsTab,
      },
    }),
    [patients, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: 'Active Demographics Patients', // Translation not needed
    initialTab: 'all',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: LargeTableProps = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
    patients,
  };

  return isSmallMode ? (
    <SmallModePatientsList<Tab> {...smallModeTableProps} trackingIdBase='active-patients-small-mode' />
  ) : (
    <LargeModeTable {...largeModeTableProps} />
  );
};

const LargeModeTable = ({
  isBulkMessagingEnabled,
  isDemoAccount,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  lastContactedDates,
  locationNames,
  multiLocations,
  patients,
  processExportableData,
  tabs,
}: LargeTableProps) => {
  const { t } = useTranslation('analytics');

  const colConfig: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = useMemo(() => {
    return [
      {
        Header: t('Patient Name'),
        headerLabel: t('Patient Name'),
        accessor: ({ FirstName, LastName, id, PreferredName }) =>
          JSON.stringify({
            firstName: FirstName,
            lastName: LastName,
            preferredName: PreferredName,
            userId: id,
          }),
        cellRenderer: (value: string) => {
          const props = JSON.parse(value);
          return <UserCard {...props} key={props.userId} openProfileOnClick={!isDemoAccount} showOnlyName />;
        },
        id: 'name',
        sticky: 'left' as AnalyticsCommonTypes.Alignment,
        width: 250,
      },
      {
        accessor: ({ locationId }) => locationId,
        cellRenderer: (locationId: string) => {
          return locationId ? (
            <LocationChip locationName={locationNames[locationId] || locationId} maxWidth={180} />
          ) : (
            '-'
          );
        },
        disableSortBy: true,
        Header: t('Location'),
        id: 'locationName',
        omit: !multiLocations,
        width: 220,
      },
      {
        Header: t('Phone Number'),
        headerLabel: t('Phone Number'),
        accessor: ({ HomePhone, MobilePhone, WorkPhone }) => MobilePhone || HomePhone || WorkPhone,
        cellRenderer: (value: string) => (value ? formatPhoneNumber(value) : '-'),
        id: 'phone-number',
        width: 180,
      },
      {
        Header: t('Gender'),
        headerLabel: t('Gender'),
        accessor: ({ Gender }) => Gender || '-',
        id: 'gender',
        width: 130,
      },
      {
        Header: t('Age'),
        headerLabel: t('Age'),
        accessor: ({ age }) => age || '-',
        id: 'age',
        width: 100,
      },
      {
        Header: t('Status'),
        headerLabel: t('Status'),
        accessor: ({ nextVisit }) => nextVisit,
        cellRenderer: (value: string) => <Status status={value ? 'scheduled' : 'unscheduled'} />,
        id: 'status',
        width: 150,
      },
      {
        Header: (
          <ColumnHeaderInfoTip
            columnTitle={t('Last Contacted')}
            infoTip={
              <InfoTipPopover>
                {t('Date and Time of last outbound answered call to patient phone number')}
              </InfoTipPopover>
            }
          />
        ),
        headerLabel: t('Last Contacted'),
        accessor: ({ id }) => formatters.date(lastContactedDates?.[id]),
        cellRenderer: (lastContacted: string) => (isLoadingLastContacted ? <TableLoadingSkeleton /> : lastContacted),
        id: 'lastContacted',
        width: 180,
      },
      {
        Header: t('Insurance'),
        headerLabel: t('Insurance'),
        accessor: ({ id }) => patients.insurances?.[id] || '-',
        id: 'insurance',
        width: 150,
      },
      {
        disableSortBy: true,
        Header: t('Actions'),
        headerLabel: t('Actions'),
        headerAlign: 'center' as AnalyticsCommonTypes.Alignment | 'center',
        accessor: (patient) => patient,
        cellRenderer: (patient) => {
          const phoneNumber = patient.MobilePhone || patient.HomePhone;
          return (
            <TableActions
              isDemoAccount={isDemoAccount}
              personId={patient.id}
              personName={patient.FirstName}
              phoneNumber={phoneNumber}
              trackingIdBase='active-patient'
            />
          );
        },
        id: 'actions',
        sticky: 'right' as AnalyticsCommonTypes.Alignment,
        width: 110,
      },
    ];
  }, [
    t,
    multiLocations,
    isDemoAccount,
    locationNames,
    lastContactedDates,
    isLoadingLastContacted,
    patients.insurances,
  ]);

  const tabsCommonConfig = {
    colConfig,
    emptyStateConfig: 'users' as keyof typeof emptyStateGraphics,
  };

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      isDemoAccount={isDemoAccount}
      exportFileName='Active Demographics Patients' // Translation not needed
      initialTab='all'
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      processExportableData={processExportableData}
      showExportIcon={isExportEnabled}
      tabs={{
        all: {
          ...tabsCommonConfig,
          ...tabs.all,
          tableInstanceId: 'all-active-patients',
        },
        unscheduled: {
          ...tabsCommonConfig,
          ...tabs.unscheduled,
          tableInstanceId: 'active-unscheduled-patients',
        },
      }}
      trackingIdBase='active-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
